import { useEffect, useState, useCallback } from "react";
import { subscribeToStock } from "@/lib/realtime";
import { ff } from "@/lib/feature-flags";

/**
 * Hook to get real-time stock levels for a list of product IDs.
 *
 * Returns a map of productId → stock count, updated live via
 * Supabase Realtime when ff.realtimeUpdates is ON.
 */
export function useRealtimeStock(
  productIds: string[]
): Map<string, number> {
  const [stockMap, setStockMap] = useState<Map<string, number>>(new Map());

  const handleStockChange = useCallback(
    (productId: string, newStock: number) => {
      if (productIds.includes(productId)) {
        setStockMap((prev) => {
          const next = new Map(prev);
          next.set(productId, newStock);
          return next;
        });
      }
    },
    [productIds]
  );

  useEffect(() => {
    if (!ff.realtimeUpdates) return;
    if (productIds.length === 0) return;

    const unsubscribe = subscribeToStock(handleStockChange);
    return unsubscribe;
  }, [productIds, handleStockChange]);

  return stockMap;
}
